import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import Iconify from 'src/components/Iconify';
import { Switch } from 'src/@/components/ui/switch';
import { usePermissaoNotificacao, useSomNotificacao } from 'src/hooks/useNotificacoes';
import { AT } from './AlertasTarefas.design';

// O que dispara alerta, na ordem em que o usuário costuma encontrar. O texto da
// direita é o título real da notificação enviada pelo backend
// (services/tarefas/tarefasNotificacao.service.js) — se mudar lá, muda aqui.
const EVENTOS = [
  { icon: 'mdi:account-check-outline', quando: 'Te definem como responsável', msg: 'Tarefa atribuída a você' },
  { icon: 'mdi:account-multiple-plus-outline', quando: 'Te incluem como participante', msg: 'Você foi adicionado à tarefa' },
  { icon: 'mdi:plus-circle-outline', quando: 'Criam uma tarefa com você', msg: 'Nova tarefa para você' },
  { icon: 'mdi:comment-text-outline', quando: 'Alguém comenta na tarefa', msg: 'Novo comentário na tarefa' },
  { icon: 'mdi:calendar-edit-outline', quando: 'O prazo é alterado', msg: 'Prazo alterado' },
  { icon: 'mdi:bell-ring-outline', quando: 'Chega o "Avisar antes" do prazo', msg: 'Tarefa a vencer' },
  { icon: 'mdi:clock-alert-outline', quando: 'O prazo passa sem conclusão', msg: 'Tarefa vencida' },
  { icon: 'mdi:check-circle-outline', quando: 'A tarefa é concluída ou reaberta', msg: 'Tarefa concluída' },
];

const RECEBEM = ['Quem criou a tarefa', 'O responsável', 'Os participantes marcados'];

const NAO_AVISA = [
  'Editar título ou descrição',
  'Mudar a prioridade',
  'Marcar itens do checklist',
  'Mover entre estágios do meio',
];

export default function AlertasTarefasDialog({ open, onClose }) {
  const { estado: permissao, pedir } = usePermissaoNotificacao();
  const { ligado: somLigado, alternar: alternarSom } = useSomNotificacao();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const navegadorOn = permissao === 'granted';
  const navegadorBloqueado = permissao === 'denied';
  const semSuporte = permissao === 'unsupported';

  const navegadorPill = navegadorOn
    ? { txt: 'Ativado', cls: AT.pillOn }
    : navegadorBloqueado
      ? { txt: 'Bloqueado', cls: AT.pillBlocked }
      : semSuporte
        ? { txt: 'Indisponível', cls: AT.pillOff }
        : null;

  const navegadorDesc = navegadorOn
    ? 'Aparece mesmo com o painel em outra aba.'
    : navegadorBloqueado
      ? 'Libere as notificações deste site nas configurações do navegador.'
      : semSuporte
        ? 'Este navegador não oferece notificações.'
        : 'Sem isso, o alerta só aparece no sino.';

  return createPortal(
    <div className={AT.overlay} role="dialog" aria-modal="true" aria-labelledby="alertas-tarefas-title">
      <div className={AT.backdrop} onClick={onClose} />

      <div className={AT.panel}>
        <div className={AT.header}>
          <div className="flex items-start gap-3 min-w-0">
            <div className={AT.headerIcon}>
              <Iconify icon="mdi:bell-outline" width={18} />
            </div>
            <div className="min-w-0">
              <h2 id="alertas-tarefas-title" className={AT.title}>Como funcionam os alertas</h2>
              <p className={AT.subtitle}>Quem é avisado, quando, e por onde o aviso chega.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className={AT.close} aria-label="Fechar">
            <X size={20} />
          </button>
        </div>

        <div className={AT.body}>
          {/* Canais — a parte acionável: sem permissão, o toast nunca aparece */}
          <section>
            <h3 className={AT.sectionTitle}>Por onde o aviso chega</h3>
            <div className="space-y-2">
              <div className={AT.channel}>
                <div className={`${AT.channelIcon} ${AT.channelIconOn}`}>
                  <Iconify icon="solar:bell-bing-bold-duotone" width={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={AT.channelName}>Sino do painel</p>
                  <p className={AT.channelDesc}>Clique no aviso para abrir a tarefa.</p>
                </div>
                <span className={`${AT.pill} ${AT.pillOn}`}>Sempre ativo</span>
              </div>

              <div className={AT.channel}>
                <div className={`${AT.channelIcon} ${navegadorOn ? AT.channelIconOn : AT.channelIconOff}`}>
                  <Iconify icon="mdi:monitor-shimmer" width={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={AT.channelName}>Notificação do navegador</p>
                  <p className={AT.channelDesc}>{navegadorDesc}</p>
                </div>
                {navegadorPill ? (
                  <span className={`${AT.pill} ${navegadorPill.cls}`}>{navegadorPill.txt}</span>
                ) : (
                  <button type="button" onClick={pedir} className={AT.channelBtn}>Ativar</button>
                )}
              </div>

              <div className={AT.channel}>
                <div className={`${AT.channelIcon} ${somLigado ? AT.channelIconOn : AT.channelIconOff}`}>
                  <Iconify icon={somLigado ? 'mdi:volume-high' : 'mdi:volume-off'} width={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={AT.channelName}>Som</p>
                  <p className={AT.channelDesc}>Toca junto com o alerta, no painel inteiro.</p>
                </div>
                <Switch
                  checked={somLigado}
                  onCheckedChange={alternarSom}
                  aria-label="Som das notificações"
                  className="data-[state=checked]:bg-[#7C3AED]"
                />
              </div>

              <div className={AT.channel}>
                <div className={`${AT.channelIcon} ${AT.channelIconOn}`}>
                  <Iconify icon="mdi:cellphone" width={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={AT.channelName}>App no celular</p>
                  <p className={AT.channelDesc}>Push chega com o app fechado, se você estiver logado nele.</p>
                </div>
                <span className={`${AT.pill} ${AT.pillOn}`}>Automático</span>
              </div>
            </div>
          </section>

          {/* Eventos */}
          <section>
            <h3 className={AT.sectionTitle}>Quando você é avisado</h3>
            <div className={AT.eventList}>
              {EVENTOS.map(ev => (
                <div key={ev.msg + ev.quando} className={AT.eventRow}>
                  <Iconify icon={ev.icon} className={AT.eventIcon} width={16} />
                  <span className={AT.eventWhen}>{ev.quando}</span>
                  <span className={AT.eventMsg}>{ev.msg}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Quem recebe / o que não avisa */}
          <section className={AT.cols}>
            <div className={AT.note}>
              <p className={AT.noteTitle}>Quem recebe</p>
              <div className="space-y-1.5">
                {RECEBEM.map(item => (
                  <p key={item} className={AT.noteItem}>
                    <span className={AT.noteDot} />
                    {item}
                  </p>
                ))}
              </div>
              <p className="mt-2.5 text-[12px] text-[#71717a] dark:text-[#ABABAD] leading-relaxed">
                Quem fez a ação nunca é avisado dela.
              </p>
            </div>

            <div className={AT.note}>
              <p className={AT.noteTitle}>O que não gera alerta</p>
              <div className="space-y-1.5">
                {NAO_AVISA.map(item => (
                  <p key={item} className={AT.noteItem}>
                    <span className={AT.noteDot} />
                    {item}
                  </p>
                ))}
              </div>
              <p className="mt-2.5 text-[12px] text-[#71717a] dark:text-[#ABABAD] leading-relaxed">
                Essas mudanças só atualizam o quadro.
              </p>
            </div>
          </section>
        </div>

        <div className={AT.footer}>
          <p className={AT.footerHint}>
            O lembrete de prazo é escolhido em cada tarefa, no campo <strong className="font-semibold text-[#3f3f46] dark:text-[#D1D2D3]">Avisar antes</strong>.
          </p>
          <button type="button" onClick={onClose} className={AT.primaryBtn}>Entendi</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
